export interface AdminPanelConfig { url: string; crmOrigin: string; }

export function mayEnterAdmin(permissions: Readonly<Record<string, readonly string[]>>): boolean {
  return permissions['admin-panel']?.includes('access') === true
    || permissions['transporting_units']?.includes('manage-fields') === true;
}

/** No credentials in URLs or broadcasts; retain the opener only for this bounded handshake. */
export function openAdminSessionBridge(
  host: Window, config: AdminPanelConfig, getToken: () => string | null,
  allowed: () => boolean, fail: (message: string) => void,
): () => void {
  const target = new URL(config.url);
  if (host.location.origin !== config.crmOrigin || target.protocol !== 'https:'
    || target.username || target.password || target.search || target.hash || target.pathname !== '/') {
    fail('Admin is not configured for this CRM.');
    return () => {};
  }
  if (!allowed() || !getToken()) return () => {};
  const popup = host.open(`${target.origin}/session-handoff`, '_blank');
  if (!popup) {
    fail('Allow pop-ups for this CRM to open Admin.');
    return () => {};
  }
  const openedAt = Date.now();
  let active = true;
  const stop = () => {
    active = false;
    host.removeEventListener('message', receive);
    host.clearTimeout(timer);
  };
  const receive = (event: MessageEvent) => {
    if (!active || event.origin !== target.origin || event.source !== popup) return;
    const data = event.data;
    if (!data || data.type !== 'c2b-admin-ready' || typeof data.challenge !== 'string'
      || !/^[A-Za-z0-9_-]{32,128}$/.test(data.challenge)) return;
    const token = getToken();
    if (Date.now() - openedAt >= 30_000 || !allowed() || !token || popup.closed) {
      stop();
      fail('The Admin session request expired. Open Admin again.');
      return;
    }
    stop();
    popup.postMessage({type: 'c2b-admin-session', challenge: data.challenge, token}, target.origin);
  };
  const timer = host.setTimeout(() => {
    stop();
    fail('Admin did not respond. Open Admin again.');
  }, 30_000);
  host.addEventListener('message', receive);
  return stop;
}
